class ImageContainerResizer {
  constructor() {
    this.updateImageContainerHeight = this.updateImageContainerHeight.bind(this); // Bind context
    this.handleImageLoad = this.handleImageLoad.bind(this);

    this.targets = [
      {
        imageSelector: ".lc-infobox__image",
        containerSelector: ".lc-infobox__col--image",
          heightMultiplier: 1,
          maxHeightToViewport: true
      },
      {
        imageSelector: ".lc-infobox-popup__video-player",
        containerSelector: ".lc-infobox-popup__video , .lc-popup-video__container",
        // Make popup video intentionally taller than the intrinsic media height.
        heightMultiplier: 1.4,
        applyHeightToMedia: true
      }
    ];

    this.boundImages = new Set();
    this.bindImageLoadEvents();
    this.updateImageContainerHeight(); // Initial update

    window.addEventListener("resize", this.updateImageContainerHeight);
    window.addEventListener("orientationchange", this.updateImageContainerHeight);
  }

  bindImageLoadEvents() {
    const images = this.getAllTargetImages();

    images.forEach(image => {
      if (this.boundImages.has(image)) {
        return;
      }

      this.boundImages.add(image);

      if (image.tagName === "VIDEO") {
        image.addEventListener("loadedmetadata", this.handleImageLoad);
        image.addEventListener("loadeddata", this.handleImageLoad);
      } else if (!image.complete) {
        image.addEventListener("load", this.handleImageLoad);
      }
    });
  }

  handleImageLoad() {
    this.updateImageContainerHeight();
  }

  updateImageContainerHeight() {
    this.bindImageLoadEvents();

    const images = this.getAllTargetImages();

    images.forEach(image => {
      const containers = this.getContainersForImage(image);
      const target = this.getTargetForImage(image);

      if (image && containers.length > 0) {
        const imageHeight = this.getMediaHeight(image);
        const multiplier = target?.heightMultiplier || 1;
        const adjustedHeight = Math.round(imageHeight * multiplier);
          const maxHeight = target?.maxHeightToViewport
            ? this.getViewportHeightLimit()
            : null;
          const finalHeight = maxHeight ? Math.min(adjustedHeight, maxHeight) : adjustedHeight;
        const applyHeightToMedia = Boolean(target?.applyHeightToMedia);

          if (finalHeight > 0) {
          containers.forEach(container => {
              container.style.height = `${finalHeight}px`;
              container.style.maxHeight = maxHeight ? `${maxHeight}px` : "";
          });

            if (applyHeightToMedia || target?.maxHeightToViewport) {
              image.style.maxHeight = maxHeight ? `${maxHeight}px` : "";
            }

            if (applyHeightToMedia) {
              image.style.height = `${finalHeight}px`;
          }
        } else {
          containers.forEach(container => {
            container.style.height = "";
              container.style.maxHeight = "";
          });

            if (applyHeightToMedia || target?.maxHeightToViewport) {
              image.style.maxHeight = "";
            }

            if (applyHeightToMedia) {
            image.style.height = "";
          }
        }
      }
    });
  }

  getAllTargetImages() {
    const selectors = this.targets.map(target => target.imageSelector).join(", ");
    return document.querySelectorAll(selectors);
  }

  getMediaHeight(media) {
    const rect = media.getBoundingClientRect();
    const width = rect.width || media.clientWidth;

    if (media.tagName === "VIDEO") {
      if (width > 0 && media.videoWidth > 0 && media.videoHeight > 0) {
        return width * (media.videoHeight / media.videoWidth);
      }

      if (rect.height > 0) {
        return rect.height;
      }

      return 0;
    }

    if (rect.height > 0) {
      return rect.height;
    }

    if (width <= 0) {
      return 0;
    }

    if (media.naturalWidth > 0 && media.naturalHeight > 0) {
      return width * (media.naturalHeight / media.naturalWidth);
    }

    return 0;
  }

  getContainersForImage(image) {
    const target = this.getTargetForImage(image);
    if (!target || !target.containerSelector) {
      return [];
    }

    const selectors = target.containerSelector
      .split(",")
      .map(selector => selector.trim())
      .filter(Boolean);

    const containers = selectors
      .map(selector => image.closest(selector))
      .filter(container => container !== null);

    return Array.from(new Set(containers));
  }

  getTargetForImage(image) {
    for (const target of this.targets) {
      if (image.matches(target.imageSelector)) {
        return target;
      }
    }

    return null;
  }

  getViewportHeightLimit() {
    if (!window.matchMedia("(min-width: 1200px)").matches) {
      return null;
    }

    const headerHeightValue = getComputedStyle(document.documentElement)
      .getPropertyValue("--theme-header-height")
      .trim();
    const headerHeight = Number.parseFloat(headerHeightValue);
    const resolvedHeaderHeight = Number.isFinite(headerHeight) ? headerHeight : 0;
    const availableHeight = window.innerHeight - resolvedHeaderHeight;

    return availableHeight > 0 ? availableHeight : null;
  }

  unsubscribe() {
    this.boundImages.forEach(image => {
      if (image.tagName === "VIDEO") {
        image.removeEventListener("loadedmetadata", this.handleImageLoad);
        image.removeEventListener("loadeddata", this.handleImageLoad);
      } else {
        image.removeEventListener("load", this.handleImageLoad);
      }
    });

    this.boundImages.clear();

    window.removeEventListener("resize", this.updateImageContainerHeight);
    window.removeEventListener("orientationchange", this.updateImageContainerHeight);
  }
}

export default ImageContainerResizer;
